import { createContext, useState, useEffect, useCallback, useMemo } from "react";

export const CheckOutContext = createContext();

// Constants for localStorage keys
const CHECKOUT_DATA_KEY = 'checkOutData';
const CHECKOUT_SUBTOTAL_KEY = 'checkOutSubTotal';

export const CheckOutProvider = ({children}) =>{
    // Initialize from localStorage with better error handling
    const initializeCheckOutData = () => {
        try {
            const savedData = localStorage.getItem(CHECKOUT_DATA_KEY);
            if (!savedData) return [];
            
            const parsed = JSON.parse(savedData);
            // Validate that it's an array and has valid structure
            return Array.isArray(parsed) ? parsed.filter(item => 
                item && 
                typeof item === 'object' && 
                item.productID && 
                typeof item.price === 'number' && 
                typeof item.quantity === 'number'
            ) : [];
        } catch (error) {
            console.error('Error loading checkout data:', error);
            localStorage.removeItem(CHECKOUT_DATA_KEY);
            return [];
        }
    };

    const initializeSubTotal = () => {
        try {
            const savedSubTotal = localStorage.getItem(CHECKOUT_SUBTOTAL_KEY);
            const parsed = savedSubTotal ? parseFloat(savedSubTotal) : 0;
            return isNaN(parsed) ? 0 : parsed;
        } catch (error) {
            console.error('Error loading subtotal:', error);
            localStorage.removeItem(CHECKOUT_SUBTOTAL_KEY);
            return 0;
        }
    };

    const [checkOutData, setCheckOutData] = useState(initializeCheckOutData);

    // Calculate subtotal from checkout data for consistency
    const subTotal = useMemo(() => {
        return checkOutData.reduce(
            (total, item) => {
                // Ensure price and quantity are numbers
                const price = typeof item.price === 'number' ? item.price : 0;
                const quantity = typeof item.quantity === 'number' ? item.quantity : 0;
                return total + (price * quantity);
            },
            0
        );
    }, [checkOutData]);

    // Debounced localStorage save to improve performance
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            try {
                localStorage.setItem(CHECKOUT_DATA_KEY, JSON.stringify(checkOutData));
                localStorage.setItem(CHECKOUT_SUBTOTAL_KEY, subTotal.toString());
            } catch (error) {
                console.error('Error saving checkout data:', error);
            }
        }, 300); // 300ms debounce

        return () => clearTimeout(timeoutId);
    }, [checkOutData, subTotal]);

    // Improved addItemTocheckOut with duplicate checking and validation
    const addItemTocheckOut = useCallback((product, replaceAll = false) => {
        if (!product || !product.productID || typeof product.price !== 'number' || typeof product.quantity !== 'number') {
            console.warn('Invalid product data provided to addItemTocheckOut:', product);
            return;
        }

        setCheckOutData((prevData) => {
            // If replaceAll is true (Buy Now mode), replace all items with this single item
            if (replaceAll) {
                return [{
                    ...product,
                    price: Number(product.price),
                    quantity: Number(product.quantity),
                    addedAt: new Date().toISOString(),
                    buyNow: true // Flag to indicate this is a buy now item
                }];
            }
            
            // Normal add to cart behavior
            // Check if item already exists
            const existingIndex = prevData.findIndex(item => item.productID === product.productID);
            
            if (existingIndex !== -1) {
                // Update existing item quantity instead of duplicating
                const updatedData = [...prevData];
                updatedData[existingIndex] = {
                    ...updatedData[existingIndex],
                    quantity: product.quantity,
                    // Recalculate any derived fields if needed
                };
                return updatedData;
            } else {
                // Add new item
                return [...prevData, {
                    ...product,
                    // Ensure consistent data structure
                    price: Number(product.price),
                    quantity: Number(product.quantity),
                    addedAt: new Date().toISOString() // Track when item was added
                }];
            }
        });
    }, []);

    const removeItemFromCheckout = useCallback((productID) => {
        if (!productID) {
            console.warn('Invalid productID provided to removeItemFromCheckout:', productID);
            return;
        }

        setCheckOutData((prevData) => 
            prevData.filter((item) => item.productID !== productID)
        );
    }, []);

    // Enhanced clear function with optional callback
    const clearCheckout = useCallback((callback) => {
        setCheckOutData([]);
        try {
            localStorage.removeItem(CHECKOUT_DATA_KEY);
            localStorage.removeItem(CHECKOUT_SUBTOTAL_KEY);
        } catch (error) {
            console.error('Error clearing checkout data from localStorage:', error);
        }
        
        if (typeof callback === 'function') {
            callback();
        }
    }, []);

    // Additional utility functions for better management
    const updateItemQuantity = useCallback((productID, newQuantity) => {
        if (!productID || typeof newQuantity !== 'number' || newQuantity < 0) {
            console.warn('Invalid parameters for updateItemQuantity:', { productID, newQuantity });
            return;
        }

        if (newQuantity === 0) {
            removeItemFromCheckout(productID);
            return;
        }

        setCheckOutData((prevData) => 
            prevData.map(item => 
                item.productID === productID 
                    ? { ...item, quantity: newQuantity }
                    : item
            )
        );
    }, [removeItemFromCheckout]);

    const getItemCount = useMemo(() => {
        return checkOutData.reduce((count, item) => count + item.quantity, 0);
    }, [checkOutData]);

    const isItemInCheckout = useCallback((productID) => {
        return checkOutData.some(item => item.productID === productID);
    }, [checkOutData]);

    // Context value with memoization for performance
    const contextValue = useMemo(() => ({
        checkOutData,
        subTotal,
        addItemTocheckOut,
        removeItemFromCheckout,
        clearCheckout,
        updateItemQuantity,
        getItemCount,
        isItemInCheckout
    }), [
        checkOutData,
        subTotal,
        addItemTocheckOut,
        removeItemFromCheckout,
        clearCheckout,
        updateItemQuantity,
        getItemCount,
        isItemInCheckout
    ]);

    return (
        <CheckOutContext.Provider value={contextValue}>
            {children}
        </CheckOutContext.Provider>
    );
}